import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { agTypes } from "src/app/ag-types";
import { Asset, Device, IAlarm } from "src/app/models/entity.model";
import { AlarmService } from "src/app/services/alarm.service";
import { FarmerService } from "src/app/services/device.service";
import { PaymentService } from "src/app/services/payment.service";
import { ISensorData, ISpearSettings } from "src/app/services/types.service";
import { TEXT } from "src/app/texts";
import { attachSwipe, CropLabel, deviceHasCrop, deviceIcon, deviceImage, Index, LoadingState } from "src/app/util";
import { POSITION } from "../ag-plot/ag-plot";
import { FarmerCellularSpear, FarmerDeviceSensor } from "../cellular-spear/spear.model";
import { IProfileSettings, ProfileService } from "../login-page/profile-page";

import { Gesture, GestureController } from '@ionic/angular';
import { PageVisibilityService } from "src/app/services/page.service";

interface ISensorItem {
  model: ISensorData, title: string
}

@Component({
  selector: 'farmer-cellular-spear-item',
  template: `

<div class="card-container" *ngIf="isLoaded">

  <div class="flex-column card-content flex-auto" > 
    <div class="flex-row flex-space-between-center device-section flex-auto">
      <div class="flex card-header-label">
        <div class="device-label">{{name}}</div>
      </div>
      <div *ngIf="hasAlarm" style="text-transform: uppercase;" [style.color]="colors.maxColor" class='alarm-label'>
        {{text.general.alarm | translate}}
      </div>
      <ion-icon *ngIf="deviceIcon" [style.color]="hasAlarm ? colors.maxColor : ''" class="ag-device-icon" [src]="deviceIcon"></ion-icon>
    </div>

    <div *ngIf="isPaid == true && sensor" class="device-section" class="flex-row ag-values-row flex-auto"> 
      <div *ngIf="sensor.isTemperatureType" class="flex-row flex-start-center ag-value-row"> 
        <ag-sensor-value type='TEMPERATURE' [loading]="isLoading()" [status]="loadFailed() ? 'error' : sensor.temperatureData?.status" [value]="sensor.temperatureData?.value" ></ag-sensor-value>
      </div>
      <div *ngIf="isPaid == true && sensor.isMoistureType" class="flex-row flex-start-center ag-value-row"> 
        <ag-sensor-value *ngIf="alwaysHumidity && sensor.key == 'ambient'" [loading]="isLoading()" [status]="loadFailed() ? 'error' : sensor.humidityData?.status" type='MOISTURE' [value]="sensor.humidityData?.value"></ag-sensor-value>
        <ag-sensor-value *ngIf="!alwaysHumidity || sensor.key != 'ambient'" [loading]="isLoading()" [status]="loadFailed() ? 'error' : sensor.moistureData?.status" [cropType]="sensor.cropType" type='MOISTURE' [value]="sensor.moistureData?.value" ></ag-sensor-value>
      </div>
    </div>
  </div>

  <div *ngIf="isPaid == true" style="height: 100px; pointer-events: none;" class="flex-column ag-flot-row flex-auto"> 
    <ag-plot class="flex flex-column" [grid]="flotSettings.grid" [axis]="flotSettings.axes" [entity]="device.id" [timeseries]="timeseries"></ag-plot>
  </div>
  
  <div *ngIf="isPaid == true" class="flex-row device-section">
    <div *ngFor="let sensor of sensors" 
         [class]="{active: isActiveSensor(sensor)}" 
         (click)="$event.stopPropagation(); onClickSensor($event, sensor)" class="sensor-button">
          <div class="sensor-button-text">{{sensor.title | translate}}</div>
    </div>
    <div *ngIf="sensor && sensor.latestTs" class="time-value">
      <ag-time-since [ts]="sensor.latestTs"></ag-time-since>
    </div>
  </div>
</div>

  `,
  styleUrls: ['../farmer-devices/farmer-device-item.scss']
})
export class FarmerCellularSpearItem {

  @Input() device: Device | Asset
  @Input() hasAlarm: boolean
  @Output() onClick = new EventEmitter<Device>();

  // swiping the card switches the sensor, like the small buttons do
  private swipeGesture: Gesture | null = null

  private ctrl: FarmerCellularSpear
  public sensor: FarmerDeviceSensor
  sensors: FarmerDeviceSensor[] = []
  name: string
  
  alwaysHumidity = false

  timeseries = []

  emc_values: Index<string> = {}

  text = TEXT
  colors = agTypes.colors
  sampleTemp = ''
  sampleEmc = ''
  sampleMoisture = '-'
  ambientTemp = ''
  ambientEmc = ''
  ambientMoisture = '-'

  sampleUpdateTime = 0
  ambientUpdateTime = 0

  tempHistoryUnit = ''
  loadingState = new LoadingState()

  moistureCrop = ''
  flotSettings = {
    shadowSize: 0,
    axes: [
      {
        position: 'left' as POSITION, showLabels: false
      },
      {
        showLabels: false,
        alignTicksWithAxis: 1,
        position: 'right' as POSITION,
      },
      {
        position: 'bottom' as POSITION,
        showLabels: false
      }
    ],
    grid: {
      outlineWidth: 0,
      margin: 5,
      minBorderMargin: 0,
      borderColor: 'transparent'
    },
    smoothLines: true
  };

  flotKeySettings = {
    lineWidth: 1
  };
  legendConfig = {};

  cropNames = [];

  settings: ISpearSettings = {
    name: ''
  }
  deviceIcon
  isLoaded

  _sensorSubscription: Subscription
  _subscriptions: Subscription[] = []
  listen (s: Subscription) { this._subscriptions.push(s) }
  ngOnDestroy () { 
    this.swipeGesture?.destroy()
    this.swipeGesture = null
    if (this._sensorSubscription) this._sensorSubscription.unsubscribe()
    this._subscriptions.map(s => s.unsubscribe()); 
    this._subscriptions = [] 
  }
  
  constructor (
    private farmer: FarmerService,
    private payment: PaymentService,
    private alarmService: AlarmService,
    private translateService: TranslateService,
    private profile: ProfileService,
    private gestureCtrl: GestureController,
    private elementRef: ElementRef,
    private pageVisibility: PageVisibilityService
  ) {
  }

  // the values show "-" once the device has loaded without a value, nothing while loading
  isLoading () {
    return !!this.ctrl && !this.ctrl.isLoaded() && !this.ctrl.model.loading.is_failed()
  }
  loadFailed () {
    return !!this.ctrl && this.ctrl.model.loading.is_failed()
  }

  private activeIndex(): number {
    return this.sensors.findIndex(s => this.isActiveSensor(s))
  }

  nextSensor(): void {
    if (!this.sensors?.length) return
    const i = this.activeIndex()
    const next = (i + 1) % this.sensors.length
    this.setActiveSensor(this.sensors[next])
  }

  prevSensor(): void {
    if (!this.sensors?.length) return
    const i = this.activeIndex()
    const prev = (i - 1 + this.sensors.length) % this.sensors.length
    this.setActiveSensor(this.sensors[prev])
  }

  ngAfterViewInit () {
    // the gesture sits on the grid item, which also holds the click overlay above the card
    const host = this.elementRef.nativeElement as HTMLElement
    this.swipeGesture = attachSwipe(this.gestureCtrl, host.closest('.device-item') as HTMLElement || host, direction => {
      if (direction == 'left') this.nextSensor()
      else this.prevSensor()
    })
  }

  
  
  get cropLabel () {
    return CropLabel(this.sensor.cropType)
  }

  onClickSensor (evt, sensor) {
    evt.stopPropagation()
    this.setActiveSensor(sensor)
    return false
  }

  deviceHasCrop () { return deviceHasCrop(this.device.type) }
  
  setActiveSensor (sensor: FarmerDeviceSensor) {
    this.sensor = sensor
    if (this._sensorSubscription) this._sensorSubscription.unsubscribe()
    this._sensorSubscription = this.sensor.onDataUpdate.subscribe(sensor => {
      this.onSensorUpdated()
    })
    this.onSensorUpdated()
  }

  onSensorUpdated () {
    this.timeseries = []
    if (!this.sensor) return
    if (this.sensor.hasTemperature()) {
      this.timeseries.push({
        name: this.sensor.temperatureKey(),
        label: this.sensor.name + " Temp.",
        color: agTypes.colors.maxColor
      })
    }
    
    if (this.sensor.hasMoisture()) {
      if (this.sensor.key == 'ambient' && this.alwaysHumidity) {
        this.timeseries.push({
          name: this.sensor.humidityKey(),
          label: this.sensor.name + "Humidity.",
          color: agTypes.colors.minColor,
          yaxis: 2
        })
      } else {
        this.timeseries.push({
          name: this.sensor.moistureKey(),
          label: this.sensor.name + "Moisture.",
          color: agTypes.colors.minColor,
          yaxis: 2
        })
      }
    }
  }

  isActiveSensor (sensor: FarmerDeviceSensor) {
    return this.sensor && this.sensor.key == sensor.key
  }

  initSpear () {
    //console.log('init spear item', this.device.label)
    this.deviceIcon = deviceIcon(this.device.type)
    this.name = this.ctrl.settings.label
    this.sensors = this.ctrl.sensors
    if (!this.sensor && this.ctrl.sensors[0]) {
      this.setActiveSensor(this.ctrl.sensors[0])
    }
    this.isLoaded = true
    this.loadingState.success()
  }
  
  isPaid = null
  
  ngOnInit () {
    this.ctrl = this.farmer.getCellularSpear(this.device as Device)


    this.listen(this.pageVisibility.whileVisible(
      this.elementRef.nativeElement, 
      () => this.ctrl.subscribe(() => {
        this.initSpear()
      }),
      {label: 'device card: ' + this.device.label, viewport: true}
    ))

    /*this.listen(
      this.ctrl.model.onUpdate.subscribe(x => { this.initSpear() })
    )*/
    this.listen(this.profile.settings.subscribe(profileSettings => {
      this.alwaysHumidity = profileSettings.is_ambient_humidity      
      this.initSpear()
      this.onSensorUpdated()
    }))
    
    this.listen(
      this.payment.subscribe(() => {
        this.isPaid = this.payment.getEntityState(this.device)?.active
      } )
    )
    
    if (this.ctrl.model.isLoaded()) this.initSpear()
  }

  getCropName (cropNameId) {
      if (this.cropNames && this.cropNames.length) {
          for (let i = 0; i < this.cropNames.length; i++) {
              if (this.cropNames[i].nameId === cropNameId) {
                  return this.cropNames[i].value;
              }
          }
      }
      return "-";
  }

  getDevicePic() { return deviceImage(this.device.type) }
}
